
import { ComponentBase } from "../core/ComponentBase.js";
import { DataForm } from "./DataForm.js";
import { jsonSchemaToZod } from "json-schema-to-zod";

export class VisualWorkflowBuilder extends ComponentBase {
    // Provide the default configuration for toggling various features.
    static getDefaultConfig() {
        return {
            toggleMap: true,
            toggleZoom: true,
            toggleCanvasGrid: true,
            toggleLayoutButton: true,
            defaultLayout: "grid",
            layoutOptions: ["grid", "vertical", "horizontal"],
            autoCollapsePropertySidebar: true,
        };
    }

    static styles = `
    .visual-workflow-builder {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: var(--bs-body-bg);
      color: var(--bs-body-color);
      display: none; /* Hidden by default */
      flex-direction: column;
      opacity: 0;
      transition: opacity var(--ab-t-base);
      z-index: var(--ab-z-modal);
    }
    .visual-workflow-builder.visible {
      display: flex;
      opacity: 1;
    }
    .workflow-header {
      padding: 0.5rem 1rem;
      border-bottom: 1px solid var(--bs-border-color);
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;
    }
    .workflow-header .title {
      font-size: var(--ab-text-md);
      font-weight: var(--ab-weight-medium);
      margin: 0;
    }
    .workflow-body {
      display: flex;
      flex-grow: 1;
      overflow: hidden;
    }
    .workflow-builder-sidebar {
      border: 1px solid var(--bs-border-color);
      padding: 0.5rem;
      overflow-y: auto;
      background-color: var(--bs-tertiary-bg);
      color: var(--bs-body-color);
      transition: width 0.3s ease;
      position: relative;
    }
    .workflow-builder-sidebar.collapsed {
      width: 40px !important;
    }
    .workflow-builder-sidebar.collapsed > .sidebar-header span {
      display: none;
    }
    .workflow-builder-sidebar.collapsed > div:not(.sidebar-header) {
      display: none;
    }
    .toggle-sidebar {
      background: none;
      border: none;
      outline: none;
      padding: 0;
      margin-left: 0.5rem;
      cursor: pointer;
    }
    .toggle-sidebar i {
      font-size: var(--ab-text-md);
    }
    .tool-palette-sidebar {
      max-width: 265px;
    }
    .property-sidebar {
      width: 250px;
      max-width: 250px;
    }
    .sidebar-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }
    .workflow-builder-canvas {
      flex: 1;
      position: relative;
      overflow: hidden;
      background-color: var(--bs-secondary-bg, #f0f0f0);
    }
    .tool-palette-container {
      display: flex;
      flex-wrap: wrap;
      gap: 0.2rem;
    }
    .tool-category-container {
      display: flex;
      flex-wrap: wrap;
      gap: 0.2rem;
    }
    .tool-item {
      width: 75px;
      height: 75px;
      border: 1px solid var(--bs-border-color);
      border-radius: var(--ab-radius-sm);
      padding: 0.2rem;
      cursor: grab;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      font-size: var(--ab-text-2xs);
      color: var(--bs-body-color);
      transition: background-color var(--ab-t-fast), transform var(--ab-t-fast), box-shadow var(--ab-t-fast);
    }
    @media (hover: hover) {
      .tool-item:hover {
        background-color: var(--ab-hover-bg);
        transform: var(--ab-lift-sm);
        box-shadow: var(--ab-shadow-xs);
      }
    }
    .tool-category-header {
      display:flex;
      justify-content: space-between;
      align-items: center;
      width:100%;
      margin-bottom: 0.5rem;
      color: var(--bs-body-color);
    }
    .zoom-controls {
      position: absolute;
      top: 10px;
      right: 10px;
      display: flex;
      align-items: center;
      gap: 0.25rem;
      z-index: var(--ab-z-raised);
    }
    .zoom-controls button,
    .zoom-controls select {
      font-size: var(--ab-text-xs);
      padding: 0.25rem 0.5rem;
    }
    .zoom-level {
      font-size: var(--ab-text-sm);
      font-weight: var(--ab-weight-bold);
      color: var(--bs-secondary);
    }
    .area-map {
      background: var(--bs-white);
      border: 1px solid var(--bs-border-color);
    }
    .workflow-copilot-modal .modal-body {
      height: 600px;
      padding: 0;
      overflow: hidden;
    }
    .visual-workflow-builder button:focus-visible,
    .visual-workflow-builder select:focus-visible,
    .visual-workflow-builder input:focus-visible {
      outline: none;
      box-shadow: var(--ab-focus-ring);
    }
`;


    constructor(config) {
        super(config);
        // (these properties are used in various methods)
        this.workflowData = null;
        this.modal = null;
        this.graph = null;
        this.availableTools = [];
        this.selectedNode = null;
        this.propertyForm = null;
        this.copiedNodeData = null;
        this.pendingTools = null;
        this.X6 = null;
        this.isDirty = false;
        this._boundKeyboardHandler = this.handleKeyboardShortcuts.bind(this);
        this.applyStyles();
    }


    setDirty(flag) {
        this.isDirty = flag;
        const saveBtn = this.container.querySelector(`#${this.config.instanceId}-save-btn`);
        if (saveBtn) {
            saveBtn.disabled = !flag;
            // (Optionally update styling to indicate “dirty” state.)
        }
    }

    validateWorkflow() {
        let valid = true;
        const errors = [];
        const nodes = this.graph.getNodes();

        nodes.forEach(node => {
            const data = node.getData();
            const toolDef = this.availableTools.find(t => t.name === data.name);
            if (!toolDef) return;
            let nodeHasError = false;

            // (1) For non-StartTool nodes, check input port "in" is connected.
            if (toolDef.className !== "StartTool") {
                const incomingEdges = this.graph.getIncomingEdges(node) || [];
                const inputConnected = incomingEdges.some(edge => edge.getTargetPortId() === "in");
                if (!inputConnected) {
                    valid = false;
                    nodeHasError = true;
                    errors.push(`${data.name} (ID: ${data.instanceId}): Input port "in" is not connected.`);
                }
            }

            // (2) For output ports validation.
            if (toolDef.dynamicLeads) {
                // For tools with dynamic leads, use the dynamic leads from the node's data.
                const dynamicLeads = data.dynamicLeads || [];
                const outgoingEdges = this.graph.getOutgoingEdges(node) || [];
                dynamicLeads.forEach(lead => {
                    const foundEdge = outgoingEdges.some(edge => edge.getSourcePortId() === lead);
                    if (!foundEdge) {
                        valid = false;
                        nodeHasError = true;
                        errors.push(`${data.name} (ID: ${data.instanceId}): Dynamic output port '${lead}' is not connected.`);
                    }
                });
            } else if (toolDef.leads && Array.isArray(toolDef.leads)) {
                // For static tools.
                const requiredOutputLeads = toolDef.leads.slice();
                const outgoingEdges = this.graph.getOutgoingEdges(node) || [];
                requiredOutputLeads.forEach(lead => {
                    const foundEdge = outgoingEdges.some(edge => edge.getSourcePortId() === lead);
                    if (!foundEdge) {
                        valid = false;
                        nodeHasError = true;
                        errors.push(`${data.name} (ID: ${data.instanceId}): Output port '${lead}' is not connected.`);
                    }
                });
            }

            // (3) Check mandatory properties, etc.
            if (toolDef.schema && Array.isArray(toolDef.schema.required)) {
                toolDef.schema.required.forEach(prop => {
                    if (!data.properties ||
                        data.properties[prop] === undefined ||
                        data.properties[prop] === null ||
                        data.properties[prop] === ""
                    ) {
                        valid = false;
                        nodeHasError = true;
                        errors.push(`${data.name} (ID: ${data.instanceId}): Mandatory property '${prop}' is not set.`);
                    }
                });
            }
            // Highlight node if errors found.
            if (nodeHasError) {
                node.attr("body", { stroke: 'red', strokeWidth: 2 });
            } else {
                node.attr("body", { stroke: "var(--bs-dark)", strokeWidth: 1 });
            }
        });

        if (!valid) {
            const msg = "Validation errors:\n" + errors.join("\n");
            this.appClient.notifier.alert(msg);
        }
        return valid;
    }

    // initialize: load X6 modules via dynamic import.
    async initialize() {
        await super.initialize();
        await this.ensureX6Loaded();
        this.appClient.clientCommandHandler.registerCommand("set-active-workflow-config", this.handleCmdSetActiveWorkflowConfig.bind(this));
        this.appClient.clientCommandHandler.registerCommand("fetch-active-workflow-config", this.handleCmdFetchActiveWorkflowConfig.bind(this));
        this.appClient.clientCommandHandler.registerCommand("reload-tools", this.handleCmdReloadTools.bind(this));
    }

    // Load the X6 module from CDN (version 1.35.1 in this example).
    async ensureX6Loaded() {
        //const x6Module = await import("https://cdn.jsdelivr.net/npm/@antv/x6@1.35.1/+esm");
        const x6Module = await import("https://cdn.jsdelivr.net/npm/@antv/x6@2.18.1/+esm");
        this.X6 = x6Module;

        const MiniMap = await import("https://cdn.jsdelivr.net/npm/@antv/x6-plugin-minimap@2.0.7/+esm");
        this.MiniMap = MiniMap.MiniMap;

        // const Layout = await  import("https://cdn.jsdelivr.net/npm/@antv/layout@1.2.14-beta.8/+esm");
        // this.Layout = Layout;
    }

    // initAfterLogin: called after user login to load tools and render palette.
    async initAfterLogin() {
        await this.loadAvailableTools();
        if (!this.container) await this.render();
        this.renderToolPalette();
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement("div");
            this.container.id = `${this.config.instanceId}-container`;
            this.container.className = "visual-workflow-builder";

            // Re-create the structure without modal wrappers
            this.container.innerHTML = `
            <div class="workflow-header">
                <h6 class="title">
                    ${this.t("Workflow Builder")}
                    <span class="workflow-builder-title">${this.config.title ? `(${this.config.title})` : ""}</span>
                </h6>
                <div class="d-flex gap-2 align-items-center justify-content-center">
                    <button id="${this.config.instanceId}-copilot-btn" type="button" class="ab-icon-btn" title="${this.t("Toggle CoPilot")}" aria-label="${this.t("Toggle CoPilot")}">
                        <i class="bi bi-robot"></i>
                    </button>
                    <button id="${this.config.instanceId}-save-btn" type="button" class="ab-icon-btn" title="${this.t("Save Workflow")}" aria-label="${this.t("Save Workflow")}" disabled>
                        <i class="bi bi-floppy-fill"></i>
                    </button>
                    <button id="${this.config.instanceId}-close-btn" type="button" class="ab-icon-btn text-danger" title="${this.t("Close")}" aria-label="${this.t("Close")}"><i class="bi bi-x-lg"></i></button>
                </div>
            </div>
            <div class="workflow-body">
                <div class="workflow-builder-sidebar tool-palette-sidebar" id="${this.config.instanceId}-tool-palette-sidebar">
                    <div class="sidebar-header">
                        <span>${this.t("Tool Palette")}</span>
                        <button type="button" class="ab-icon-btn ab-icon-btn-sm toggle-sidebar" id="${this.config.instanceId}-toggle-left" title="${this.t("Toggle Sidebar")}" aria-label="${this.t("Toggle Sidebar")}">
                            <i class="bi bi-chevron-left"></i>
                        </button>
                    </div>
                    <div id="${this.config.instanceId}-tool-palette-container">
                        <div id="${this.config.instanceId}-tool-palette" class="tool-palette-container"></div>
                    </div>
                </div>
                <div id="${this.config.instanceId}-canvas" class="workflow-builder-canvas"></div>
                <div class="workflow-builder-sidebar property-sidebar" id="${this.config.instanceId}-property-sidebar">
                    <div class="sidebar-header">
                        <span id="${this.config.instanceId}-property-title">${this.t("Properties")}</span>
                        <div class="d-flex gap-1">
                            <button type="button" class="ab-icon-btn ab-icon-btn-sm toggle-sidebar" id="${this.config.instanceId}-toggle-right" title="${this.t("Toggle Sidebar")}" aria-label="${this.t("Toggle Sidebar")}">
                                <i class="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                    <div id="${this.config.instanceId}-property-content">
                        <div id="${this.config.instanceId}-property-editor"></div>
                    </div>
                </div>
            </div>
        `;

            // Attach event listeners for sidebars and header buttons
            this.attachEventListeners();
        }
        return this.container;
    }

    // Show the modal and initialize the X6 graph (and extra controls) once.
    show() {
        // Append to body if it's not already there
        if (!document.body.contains(this.container)) {
            document.body.appendChild(this.container);
        }

        // Make the container visible
        this.container.classList.add('visible');

        // Initialize the graph *once* after the component is visible for the first time
        if (!this.graph) {
            const canvasEl = this.container.querySelector(`#${this.config.instanceId}-canvas`);
            this.graph = new this.X6.Graph({
                container: canvasEl,
                grid: { size: 10, visible: this.config.toggleCanvasGrid },
                background: { color: "#f0f0f0" },
                connecting: {
                    allowBlank: false,
                    anchor: "center",
                    validateConnection({ sourceCell, targetCell, sourcePort, targetPort }) {
                        if (sourcePort === "in" || targetPort !== "in") return false;
                        if (!targetCell) return false;
                        return true;
                    }
                }
            });

            // Setup graph event listeners
            this.graph.on('edge:connected', ({ edge }) => {
                if (!edge.getSourceCell() || !edge.getTargetCell()) {
                    this.graph.removeCell(edge);
                } else {
                    const sourceNode = edge.getSourceCell();
                    let data = sourceNode.getData() || {};
                    data.connections = data.connections || {};
                    data.connections[edge.getSourcePortId()] = edge.getTargetCell().getData().instanceId;
                    sourceNode.setData(data);
                    this.setDirty(true);
                }
            });

            this.graph.on('edge:removed', ({ edge }) => {
                const sourceNode = edge.getSourceCell();
                if (sourceNode) {
                    const sourcePortId = edge.getSourcePortId();
                    const data = sourceNode.getData();
                    if (data && data.connections && data.connections[sourcePortId]) {
                        delete data.connections[sourcePortId];
                        sourceNode.setData(data, { overwrite: true });
                        this.setDirty(true);
                        this.log.debug(`Cleaned up stale connection from node ${data.name} on port ${sourcePortId}`);
                    }
                }
            });

            this.graph.on('node:removed', ({ node }) => {
                const removedNodeInstanceId = node.getData()?.instanceId;
                if (!removedNodeInstanceId) return;

                // Iterate through all remaining nodes to find any that were connected to the deleted one.
                this.graph.getNodes().forEach(graphNode => {
                    const data = graphNode.getData();
                    if (data && data.connections) {
                        let connectionsModified = false;
                        for (const port in data.connections) {
                            if (data.connections[port] === removedNodeInstanceId) {
                                delete data.connections[port];
                                connectionsModified = true;
                            }
                        }
                        if (connectionsModified) {
                            graphNode.setData(data, { overwrite: true });
                            this.log.debug(`Cleaned up stale connection pointing to deleted node ${removedNodeInstanceId} from node ${data.name}`);
                        }
                    }
                });
                this.setDirty(true);
            });

            this.graph.on("node:click", ({ node }) => this.setSelectedNode(node));
            this.graph.on("blank:click", () => this.clearPropertiesPanel());
            this.graph.on("edge:contextmenu", async ({ edge, e }) => {
                e.preventDefault();
                const confirmed = await this.appClient.notifier.confirm("Delete this connection?");
                if (confirmed) {
                    this.graph.removeEdge(edge);
                    this.setDirty(true);
                }
            });

            // Setup UI features
            this.setupDragAndDrop();
            const canvas = this.container.querySelector(`#${this.config.instanceId}-canvas`);
            if (this.config.toggleZoom || this.config.toggleLayoutButton) this.setupZoomAndLayoutControls(canvas);
            if (this.config.toggleMap) this.setupAreaMap(canvas);
            this.graph.on("scale", () => this.updateZoomDisplay());

            if (this.pendingTools) {
                this.setData(this.pendingTools);
                this.pendingTools = null;
            }
        }

        // Add keyboard shortcuts
        window.addEventListener("keydown", this._boundKeyboardHandler);
    }


    hide() {
        // Hide the container and clean up
        this.container.classList.remove('visible');
        window.removeEventListener("keydown", this._boundKeyboardHandler);
        this.emit("workflowClosed");

        // Optionally remove from DOM after transition, though not strictly necessary
        setTimeout(() => {
            if (!this.container.classList.contains('visible')) {
                this.container.remove();
            }
        }, 300);
    }


    attachEventListeners() {
        const leftToggle = this.container.querySelector(`#${this.config.instanceId}-toggle-left`);
        const leftSidebar = this.container.querySelector(`#${this.config.instanceId}-tool-palette-sidebar`);
        leftToggle.addEventListener("click", () => {
            leftSidebar.classList.toggle("collapsed");
            leftToggle.innerHTML = leftSidebar.classList.contains("collapsed") ? '<i class="bi bi-chevron-right"></i>' : '<i class="bi bi-chevron-left"></i>';
        });

        const rightToggle = this.container.querySelector(`#${this.config.instanceId}-toggle-right`);
        const rightSidebar = this.container.querySelector(`#${this.config.instanceId}-property-sidebar`);
        rightToggle.addEventListener("click", () => {
            rightSidebar.classList.toggle("collapsed");
            rightToggle.innerHTML = rightSidebar.classList.contains("collapsed") ? '<i class="bi bi-chevron-left"></i>' : '<i class="bi bi-chevron-right"></i>';
        });

        this.container.querySelector(`#${this.config.instanceId}-save-btn`).addEventListener("click", async () => {
            if (!this.validateWorkflow()) return;
            this.emit("workflowSaved", this.getData());
            this.setDirty(false);
        });

        this.container.querySelector(`#${this.config.instanceId}-copilot-btn`).addEventListener("click", () => this.toggleCoPilot());
        this.container.querySelector(`#${this.config.instanceId}-close-btn`).addEventListener("click", async () => {
            if (this.isDirty) {
                const confirmed = await this.appClient.notifier.confirm("You have unsaved changes. Close without saving?");
                if (!confirmed) return;
            }
            this.hide();
        });
    }


    // Load available tools using your RPC call.
    async loadAvailableTools() {
        try {
            this.availableTools = await this.appClient.rpcClient.call("WorkflowRpc.getAvailableTools");
            this.log.info("Available tools:", this.availableTools);
        } catch (error) {
            this.log.error("Error loading available tools:", error);
        }
    }

    // Render the tool palette with search and using a flex layout.
    renderToolPalette() {
        const paletteContainer = this.container.querySelector(`#${this.config.instanceId}-tool-palette`);
        if (!paletteContainer) return;
        // Group tools by category.
        const groupedTools = {};
        this.availableTools.forEach((tool) => {
            const category = tool.category || "Other";
            if (!groupedTools[category]) {
                groupedTools[category] = [];
            }
            groupedTools[category].push(tool);
        });

        // Clear previous content and set up search input.
        paletteContainer.innerHTML = `<input type="text" id="${this.config.instanceId}-tool-search" class="form-control form-control-sm w-100 mb-2" placeholder="Search tools...">`;
        const itemsContainer = document.createElement("div");
        itemsContainer.className = "tool-palette-container";
        paletteContainer.appendChild(itemsContainer);

        // Render each category: create a header with a toggle button and a container for its tools.
        Object.keys(groupedTools).forEach((category) => {
            // Sort tools alphabetically by name.
            groupedTools[category].sort((a, b) => a.name.localeCompare(b.name));

            // Create header with a toggle button (expanded by default).
            const header = document.createElement("div");
            header.className = "tool-category-header fw-bold mt-2";
            header.innerHTML = `<span>${category}</span> <button type="button" class="btn btn-sm toggle-category"><i class="bi bi-chevron-up"></i></button>`;
            itemsContainer.appendChild(header);

            // Create the container that will hold the tool items.
            const categoryContainer = document.createElement("div");
            categoryContainer.className = "tool-category-container";
            // It is expanded (visible) by default.
            categoryContainer.style.display = "";

            groupedTools[category].forEach((tool) => {
                const toolDiv = document.createElement("div");
                toolDiv.className = "tool-item";
                toolDiv.draggable = true;
                toolDiv.dataset.type = tool.name;
                toolDiv.setAttribute("data-bs-toggle", "tooltip");
                toolDiv.setAttribute("data-bs-placement", "top");
                toolDiv.title = tool.description;
                toolDiv.innerHTML = `
              ${tool.iconClass ? `<i class="${tool.iconClass} mb-1"></i>` : ""}
              ${tool.iconImage ? `<img src="${tool.iconImage}" alt="${tool.name}" class="mb-1" style="max-width:32px; max-height:32px;"/>` : ""}
              <span>${tool.name}</span>`;
                categoryContainer.appendChild(toolDiv);
            });
            itemsContainer.appendChild(categoryContainer);

            // Add toggle functionality to collapse/expand category container.
            header.querySelector(".toggle-category").addEventListener("click", () => {
                const icon = header.querySelector("i");
                if (categoryContainer.style.display === "none") {
                    categoryContainer.style.display = "";
                    icon.className = "bi bi-chevron-up";
                } else {
                    categoryContainer.style.display = "none";
                    icon.className = "bi bi-chevron-down";
                }
            });
        });

        // Initialize Bootstrap tooltips.
        const tooltipTriggerList = [].slice.call(paletteContainer.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.forEach(function (tooltipTriggerEl) {
            new bootstrap.Tooltip(tooltipTriggerEl);
        });

        // Add search functionality.
        const searchInput = paletteContainer.querySelector(`#${this.config.instanceId}-tool-search`);
        searchInput.addEventListener("input", (event) => {
            const query = event.target.value.toLowerCase();
            const items = itemsContainer.querySelectorAll(".tool-item");
            items.forEach((item) => {
                item.style.display = item.innerText.toLowerCase().indexOf(query) >= 0 ? "flex" : "none";
            });
        });

        // Fix: Attach dragstart listener on the container holding tool items
        itemsContainer.addEventListener("dragstart", (event) => {
            const toolItem = event.target.closest(".tool-item");
            if (toolItem && toolItem.dataset.type) {
                event.dataTransfer.setData("text/plain", toolItem.dataset.type);
                this.log.info("Dragging tool:", toolItem.dataset.type);
            }
        });
    }


    // Setup drag–and–drop on the canvas to add new nodes.
    setupDragAndDrop() {
        const canvasEl = this.container.querySelector(`#${this.config.instanceId}-canvas`);
        canvasEl.addEventListener("dragover", (event) => event.preventDefault());
        canvasEl.addEventListener("drop", (event) => {
            event.preventDefault();
            const toolType = event.dataTransfer.getData("text/plain");
            const tool = this.availableTools.find((t) => t.name === toolType);
            if (!tool) {
                this.log.error(`Tool type not found: ${toolType}`);
                return;
            }
            // Calculate drop position relative to the canvas.
            const rect = canvasEl.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            this.addNodeFromTool(tool, x, y);
        });
    }

    // Create and add a new node to the graph based on a tool definition.
    addNodeFromTool(tool, x, y) {
        this.setDirty(true);
        return this.createToolNode(tool, x, y);
    }

    // ---------- REPLACEMENT: createToolNode ----------
    createToolNode(tool, x = 50, y = 50) {
        const toolDef = this.availableTools.find((t) => t.name === tool.name) || tool;
        const shape = (toolDef.style && toolDef.style.shape) || "rect";
        const baseWidth = (toolDef.style && toolDef.style.width) || 120;
        const height = (toolDef.style && toolDef.style.height) || 60;

        // Default port attrs (self-contained so we don't reference external variables)
        const defaultInPortAttrs = {
            markup: [
                { tagName: "circle", selector: "portBody" },
                { tagName: "text", selector: "portLabel" }
            ],
            attrs: {
                portBody: { r: 4, magnet: true, stroke: "var(--bs-info)", fill: "var(--bs-white)" },
                portLabel: {
                    text: "in",
                    fill: "var(--bs-dark)",
                    fontSize: 10,
                    refY: -10,
                    refX: "50%",
                    textAnchor: "middle"
                }
            }
        };

        const defaultOutPortAttrs = {
            markup: [
                { tagName: "circle", selector: "portBody" },
                { tagName: "text", selector: "portLabel" }
            ],
            attrs: {
                portBody: { r: 4, magnet: true, stroke: "var(--bs-info)", fill: "var(--bs-white)" },
                portLabel: {
                    text: "",
                    fill: "var(--bs-dark)",
                    fontSize: 10,
                    refY: 10,
                    refX: "50%",
                    textAnchor: "middle"
                }
            }
        };

        // Build initial ports items (include in-port unless StartTool)
        const portsItems = [];
        if (toolDef.className !== "StartTool") {
            portsItems.push({ id: "in", group: "in" });
        }

        // Determine output leads (same logic you've been using)
        let leads = [];
        if (toolDef.leads && Array.isArray(toolDef.leads)) {
            leads = toolDef.leads.slice();
        } else if (toolDef.connections) {
            if (Array.isArray(toolDef.connections)) leads = toolDef.connections.slice();
            else if (typeof toolDef.connections === "object") leads = Object.keys(toolDef.connections);
        }

        // Scale node width based on number of bottom leads (so bottom distribution has room)
        const spacingPerPort = (toolDef.style && toolDef.style.portSpacing) || 28;
        const neededWidth = Math.max(baseWidth, Math.min(leads.length, 8) * spacingPerPort + 40);

        // Port groups: use X6 builtin position layouts (these evenly distribute ports along the chosen side).
        // Also configure label position for left/right so labels are vertical.
        const groups = {
            in: Object.assign({}, defaultInPortAttrs, {
                position: { name: "top", args: { dy: -6 } }
            }),
            out: Object.assign({}, defaultOutPortAttrs, {
                position: { name: "bottom", args: { dy: 6 } },
                // label.position can be set per group in X6; bottom labels stay horizontal
                label: { position: { name: "bottom", args: { angle: 0 } } }
            }),
            outLeft: Object.assign({}, defaultOutPortAttrs, {
                position: { name: "left", args: { dx: -6 } },
                // rotate the label so it reads vertically along the side (negative angle)
                label: { position: { name: "left", args: { angle: -90 } } }
            }),
            outRight: Object.assign({}, defaultOutPortAttrs, {
                position: { name: "right", args: { dx: 6 } },
                // rotate the label so it reads vertically along the side (positive angle)
                label: { position: { name: "right", args: { angle: 90 } } }
            })
        };

        // Placement policy:
        // - up to 4 leads -> all on bottom (out)
        // - more than 4 -> keep first 4 on bottom, split remainder evenly between left and right
        const bottomKeep = 4;
        if (leads.length <= bottomKeep) {
            leads.forEach((lead) => {
                portsItems.push({
                    id: lead,
                    group: "out",
                    attrs: { portLabel: { text: lead } }
                });
            });
        } else {
            // first bottomKeep on bottom
            leads.slice(0, bottomKeep).forEach((lead) => {
                portsItems.push({
                    id: lead,
                    group: "out",
                    attrs: { portLabel: { text: lead } }
                });
            });

            // remaining distributed between left and right
            const remainder = leads.slice(bottomKeep);
            const half = Math.ceil(remainder.length / 2);
            const leftLeads = remainder.slice(0, half);
            const rightLeads = remainder.slice(half);

            leftLeads.forEach((lead) => {
                portsItems.push({
                    id: lead,
                    group: "outLeft",
                    // Use attrs as fallback; label.position on group will rotate it vertically
                    attrs: { portLabel: { text: lead } }
                });
            });
            rightLeads.forEach((lead) => {
                portsItems.push({
                    id: lead,
                    group: "outRight",
                    attrs: { portLabel: { text: lead } }
                });
            });
        }

        // Node data
        const nodeData = {
            instanceId: tool.instanceId || `${toolDef.name}-${Date.now()}`,
            className: toolDef.className,
            name: toolDef.name,
            properties: tool.properties ? tool.properties : {},
            connections: tool.connections || {},
            templateName: toolDef.templateName || "default"
        };

        const defaultBodyAttrs = { fill: "var(--bs-white)", stroke: "var(--bs-dark)", strokeWidth: 1 };
        const bodyAttrs =
            toolDef.style && toolDef.style.attrs && toolDef.style.attrs.body
                ? Object.assign({}, defaultBodyAttrs, toolDef.style.attrs.body)
                : defaultBodyAttrs;

        const defaultLabelAttrs = {
            textWrap: { text: toolDef.name, breakWord: true, width: neededWidth - 10 },
            fill: "var(--bs-dark)",
            fontSize: 12
        };
        const labelAttrs =
            toolDef.style && toolDef.style.attrs && toolDef.style.attrs.label
                ? Object.assign({}, defaultLabelAttrs, toolDef.style.attrs.label)
                : defaultLabelAttrs;

        return this.graph.addNode({
            shape,
            x,
            y,
            width: neededWidth,
            height,
            label: toolDef.name,
            data: nodeData,
            attrs: {
                body: bodyAttrs,
                label: labelAttrs
            },
            ports: {
                groups: groups,
                items: portsItems
            }
        });
    }
    // ---------- END createToolNode ----------

    // ---------- REPLACEMENT: updateDynamicLeads ----------
    async updateDynamicLeads(node) {
        const data = node.getData();
        const toolDef = this.availableTools.find(t => t.name === data.name);
        if (!toolDef || !toolDef.dynamicLeads) return;

        try {
            const newLeads = await this.appClient.rpcClient.call("WorkflowRpc.generateDynamicLeads", {
                toolClassName: toolDef.className,
                properties: data.properties
            });

            // current output ports (we only consider the named groups we use: out, outLeft, outRight)
            const existingPorts = node.getPorts().filter(p => p.group && ["out", "outLeft", "outRight"].includes(p.group));
            const existingIds = new Set(existingPorts.map(p => p.id));
            const newSet = new Set(newLeads);

            // Remove ports that are no longer present (and remove outgoing edges tied to them)
            for (const p of existingPorts) {
                if (!newSet.has(p.id)) {
                    // remove outgoing edges from this port
                    (this.graph.getOutgoingEdges(node) || []).forEach(edge => {
                        if (edge.getSourcePortId && edge.getSourcePortId() === p.id) {
                            this.graph.removeEdge(edge);
                        }
                    });
                    try { node.removePort(p.id); } catch (e) { /* ignore version differences */ }
                }
            }

            // Decide layout split same as createToolNode: 4 bottom, remainder split left/right
            const bottomKeep = 4;
            const spacingPerPort = (toolDef.style && toolDef.style.portSpacing) || 28;
            const baseWidth = (toolDef.style && toolDef.style.width) || 120;
            const neededWidth = Math.max(baseWidth, Math.min(newLeads.length, 8) * spacingPerPort + 40);

            // Resize node if necessary
            try {
                const size = (typeof node.size === "function") ? node.size() : (node.getBBox ? node.getBBox() : null);
                const currW = (size && size.width) ? size.width : baseWidth;
                const currH = (size && size.height) ? size.height : (toolDef.style && toolDef.style.height) || 60;
                if (currW !== neededWidth) {
                    if (typeof node.resize === "function") node.resize(neededWidth, currH);
                    else if (typeof node.setSize === "function") node.setSize(neededWidth, currH);
                }
            } catch (e) {
                // non-critical
            }

            // Helper: add port while preserving any edges that previously referenced that id
            const addPortPreserveEdges = (portId, groupName, labelText) => {
                // capture outgoing edges that reference this id (if any exist from earlier)
                const outgoing = (this.graph.getOutgoingEdges(node) || []).filter(edge => edge.getSourcePortId && edge.getSourcePortId() === portId);
                // add port (if not present)
                if (!node.getPorts().find(p => p.id === portId)) {
                    node.addPort({
                        id: portId,
                        group: groupName,
                        attrs: { portLabel: { text: labelText } }
                    });
                } else {
                    // update label if existing
                    try { node.attr('portLabel', { text: labelText }, [portId]); } catch (e) { }
                }
                // reattach outgoing edges (if we captured any by recreating them simply)
                // Note: If the previous removePort removed edges, re-create a simple edge
                for (const e of outgoing) {
                    // if edge object still exists we skip; otherwise add a replacement
                    try {
                        if (!this.graph.getCell(e.id)) {
                            // recreate edge from this node's new port to the old target
                            this.graph.addEdge({
                                source: { cell: node.id, port: portId },
                                target: { cell: e.getTargetCell().id, port: e.getTargetPortId ? e.getTargetPortId() : "in" }
                            });
                        }
                    } catch (err) {
                        // ignore recreation errors
                    }
                }
            };

            // Build desired distribution
            if (newLeads.length <= bottomKeep) {
                // all bottom
                for (const lead of newLeads) {
                    addPortPreserveEdges(lead, "out", lead);
                }
                // remove any stray ports in left/right that are not in newLeads
                node.getPorts().filter(p => p.group === "outLeft" || p.group === "outRight").forEach(p => {
                    if (!newSet.has(p.id)) {
                        try { node.removePort(p.id); } catch (e) { }
                    }
                });
            } else {
                // first bottomKeep on bottom
                const bottom = newLeads.slice(0, bottomKeep);
                const remainder = newLeads.slice(bottomKeep);
                const half = Math.ceil(remainder.length / 2);
                const left = remainder.slice(0, half);
                const right = remainder.slice(half);

                bottom.forEach(lead => addPortPreserveEdges(lead, "out", lead));
                left.forEach(lead => addPortPreserveEdges(lead, "outLeft", lead));
                right.forEach(lead => addPortPreserveEdges(lead, "outRight", lead));

                // remove any ports that are part of groups but not in newLeads
                node.getPorts().forEach(p => {
                    if (!newSet.has(p.id) && ["out", "outLeft", "outRight"].includes(p.group)) {
                        try { node.removePort(p.id); } catch (e) { }
                    }
                });
            }

            // finally store dynamicLeads on node data
            const currentData = node.getData();
            currentData.dynamicLeads = newLeads;
            node.setData(currentData);

        } catch (error) {
            console.error("Error updating dynamic leads:", error);
        }
    }
    // ---------- END updateDynamicLeads ----------


    // Keyboard shortcuts for delete, copy, cut, paste.
    handleKeyboardShortcuts(event) {
        if (!this.selectedNode) return;
        if (event.key === "Delete") {
            this.graph.removeNode(this.selectedNode.id);
            this.selectedNode = null;
            this.clearPropertiesPanel();
            this.setDirty(true);
            event.preventDefault();
        }
        if (event.ctrlKey && event.key.toLowerCase() === "c") {
            this.copiedNodeData = JSON.parse(JSON.stringify(this.selectedNode.getData()));
            event.preventDefault();
        }
        if (event.ctrlKey && event.key.toLowerCase() === "x") {
            this.copiedNodeData = JSON.parse(JSON.stringify(this.selectedNode.getData()));
            this.graph.removeNode(this.selectedNode.id);
            this.selectedNode = null;
            this.clearPropertiesPanel();
            event.preventDefault();
        }
        if (event.ctrlKey && event.key.toLowerCase() === "v") {
            if (this.copiedNodeData) {
                const toolType = this.copiedNodeData.name;
                const tool = this.availableTools.find((t) => t.name === toolType);
                if (tool) {
                    const cloneData = JSON.parse(JSON.stringify(this.copiedNodeData));
                    cloneData.instanceId = `${toolType}-${Date.now()}`;
                    this.graph.addNode(Object.assign({}, cloneData, { x: 50, y: 50, label: cloneData.name }));
                }
            }
            event.preventDefault();
        }
    }

    // When a node is clicked, update the properties panel and highlight it.
    setSelectedNode(node) {
        if (this.selectedNode && this.selectedNode.id !== node.id) {
            this.selectedNode.attr("body", { stroke: "var(--bs-dark)", strokeWidth: 1 });
        }
        this.selectedNode = node;
        this.selectedNode.attr("body", { stroke: "#007bff", strokeWidth: 2 });
        this.updatePropertiesPanel();
    }

    async updatePropertiesPanel() {
        const node = this.selectedNode;
        const propertyContainer = this.container.querySelector(
            `#${this.config.instanceId}-property-editor`
        );
        const propertyTitle = this.container.querySelector(
            `#${this.config.instanceId}-property-title`
        );

        if (!node) return;

        propertyTitle.innerText = node.getData().name;
        const propContentEl = this.container.querySelector(
            `#${this.config.instanceId}-property-content`
        );
        if (propContentEl && !propContentEl.classList.contains("show")) {
            new bootstrap.Collapse(propContentEl, { toggle: true });
        }

        const toolConfig = this.availableTools.find(
            (t) => t.name === node.getData().name
        );
        if (toolConfig && toolConfig.formConfig) {
            if (this.propertyForm) {
                this.propertyForm.destroy();
                this.propertyForm = null;
            }

            const propertyFormConfig = {
                ...toolConfig.formConfig,
                hideHeader: true,
                actions: false,
                layout: "propertyGrid",
                containerClass: "property-grid",
            };

            if (toolConfig.schema) {
                // ... (existing schema conversion logic is unchanged) ...
                const generatedCode = jsonSchemaToZod(toolConfig.schema, { module: "esm" });
                const blob = new Blob([generatedCode], { type: "text/javascript" });
                const blobUrl = URL.createObjectURL(blob);
                const zodModule = await import(blobUrl);
                propertyFormConfig.schema = zodModule.default;
            }

            this.propertyForm = new DataForm({
                appClient: this.appClient,
                eventGroup: `${this.config.instanceId}-property-form-${node.getData().instanceId}`,
                ...propertyFormConfig
            });

            await this.propertyForm.initialize();
            const formEl = await this.propertyForm.render();
            propertyContainer.innerHTML = "";
            propertyContainer.appendChild(formEl);
            const data = node.getData();
            if (data.properties) {
                const props = data.properties;
                await this.propertyForm.setData(props);
            }

            // --- START OF THE FIX ---
            // Listen for 'input' or 'change' events on the form to auto-save properties to the node.
            // Using 'input' is generally more responsive for text fields, while 'change' works well for selects.
            // We can listen to both for robustness.
            const autoSaveHandler = async (event) => {
                // No need for validation here, as it's an intermediate update. Validation happens on Save.
                const updatedProperties = await this.propertyForm.getData();
                const currentData = node.getData();
                currentData.properties = updatedProperties;
                node.setData(currentData); // Update the node's data model in real-time

                let toolDef = this.availableTools.find(t => t.name === node.getData().name);
                if (toolDef && toolDef.dynamicLeads) {
                    await this.updateDynamicLeads(node);
                }

                this.setDirty(true); // Mark the workflow as having unsaved changes
            };

            this.propertyForm.formElement?.addEventListener("input", autoSaveHandler);
            this.propertyForm.formElement?.addEventListener("change", autoSaveHandler);
            // --- END OF THE FIX ---

            const nodeData = node.getData();
            const copyButtonClass = "copy-instance-id-button";
            const parent = propertyTitle.parentNode;
            Array.from(parent.querySelectorAll(`.${copyButtonClass}`)).forEach(btn => btn.remove());

            const copyBtn = document.createElement("button");
            copyBtn.type = "button";
            copyBtn.className = `btn btn-sm btn-outline-secondary ms-2 ${copyButtonClass}`;
            copyBtn.innerText = "Copy Instance ID";
            copyBtn.addEventListener("click", () => {
                navigator.clipboard.writeText(nodeData.instanceId)
                    .then(() => {
                        this.appClient.notifier.notify("Instance ID copied to clipboard!");
                    })
                    .catch((err) => {
                        this.appClient.notifier.error("Failed to copy instance ID");
                    });
            });
            parent.insertBefore(copyBtn, propertyTitle.nextSibling);

        } else {
            propertyContainer.innerHTML = `<div class="p-2 text-muted">No configuration available for this tool.</div>`;
        }
    }


    clearPropertiesPanel() {
        const propertyContainer = this.container.querySelector(`#${this.config.instanceId}-property-editor`);
        if (propertyContainer) propertyContainer.innerHTML = "";
        const propertyTitle = this.container.querySelector(`#${this.config.instanceId}-property-title`);
        if (propertyTitle) propertyTitle.innerText = "Properties";

        const parent = propertyTitle.parentNode;
        Array.from(parent.querySelectorAll(".copy-instance-id-button")).forEach(btn => btn.remove());

    }

    setData(tools) {
        if (!tools || !Array.isArray(tools)) return;
        if (!this.graph) {
            this.pendingTools = tools;
            return;
        }
        this.graph.clearCells();
        const instanceMap = {};
        tools.forEach((tool) => {
            const node = this.createToolNode(tool, tool.x, tool.y);
            instanceMap[tool.instanceId] = node;
        });

        // Reconnect nodes based on stored connections.
        tools.forEach((tool) => {
            const sourceNode = instanceMap[tool.instanceId];
            if (sourceNode && tool.connections) {
                Object.entries(tool.connections).forEach(([port, targetInstanceId]) => {
                    const targetNode = instanceMap[targetInstanceId];
                    if (targetNode) {
                        this.graph.addEdge({
                            source: { cell: sourceNode.id, port },
                            target: { cell: targetNode.id, port: "in" }
                        });
                    }
                });
            }
        });

        // Now for each node, if it supports dynamic leads, update its ports.
        Object.values(instanceMap).forEach(async (node) => {
            let def = this.availableTools.find(t => t.name === node.getData().name);
            if (def && def.dynamicLeads) {
                await this.updateDynamicLeads(node);
            }
        });

    }

    // Retrieve the current workflow data.
    getData() {
        if (!this.graph) return [];
        const nodes = this.graph.getNodes();
        const tools = nodes.map((node) => {
            const data = node.getData();
            return {
                instanceId: data.instanceId,
                className: data.className,
                name: data.name,
                properties: data.properties || {},
                connections: data.connections || {},
                x: node.position().x,
                y: node.position().y,
                templateName: data.templateName
            };
        });
        return tools;
    }

    /* ───────── Layouts ─────────
       Three simple layout functions are provided: grid, vertical and horizontal.
    */

    // Grid layout (arrange nodes in a square grid)
    applyGridLayout() {
        const nodes = this.graph.getNodes();
        const colCount = Math.ceil(Math.sqrt(nodes.length));
        nodes.forEach((node, idx) => {
            const col = idx % colCount;
            const row = Math.floor(idx / colCount);
            node.position(col * 150 + 50, row * 100 + 50);
        });
    }

    // Vertical layout (nodes arranged down a single column)
    applyVerticalLayout() {
        const nodes = this.graph.getNodes();
        nodes.forEach((node, idx) => {
            node.position(100, idx * 100 + 50);
        });
    }

    // Horizontal layout (nodes arranged in a row)
    applyHorizontalLayout() {
        const nodes = this.graph.getNodes();
        nodes.forEach((node, idx) => {
            node.position(idx * 150 + 50, 100);
        });
    }

    // Setup zoom controls and layout select dropdown.
    setupZoomAndLayoutControls(canvasEl) {
        const zoomControls = document.createElement("div");
        zoomControls.className = "zoom-controls";
        // Create zoom buttons and a span to show the current zoom level.
        zoomControls.innerHTML = `
      ${this.config.toggleZoom ? `<button id="${this.config.instanceId}-zoom-in" class="btn btn-secondary btn-sm">+</button>` : ""}
      ${this.config.toggleZoom ? `<span id="${this.config.instanceId}-zoom-level" class="zoom-level">100%</span>` : ""}
      ${this.config.toggleZoom ? `<button id="${this.config.instanceId}-zoom-out" class="btn btn-secondary btn-sm">-</button>` : ""}
      ${this.config.toggleZoom ? `<button id="${this.config.instanceId}-zoom-reset" class="btn btn-secondary btn-sm">Reset</button>` : ""}
      ${this.config.toggleLayoutButton ? `<select id="${this.config.instanceId}-layout-select" class="form-select form-select-sm bg-secondary" style="width:auto;">
            ${this.config.layoutOptions.map(l => `<option value="${l}" ${l === this.config.defaultLayout ? "selected" : ""}>${l}</option>`).join("")}
          </select>` : ""}
    `;
        canvasEl.appendChild(zoomControls);

        if (this.config.toggleZoom) {
            this.container.querySelector(`#${this.config.instanceId}-zoom-in`).addEventListener("click", () => {
                const currentZoom = this.graph.zoom();
                this.graph.zoomTo(currentZoom + 0.1);
                this.updateZoomDisplay();
            });

            this.container.querySelector(`#${this.config.instanceId}-zoom-out`).addEventListener("click", () => {
                const currentZoom = this.graph.zoom();
                this.graph.zoomTo(currentZoom - 0.1);
                this.updateZoomDisplay();
            });

            this.container.querySelector(`#${this.config.instanceId}-zoom-reset`).addEventListener("click", () => {
                this.graph.zoomTo(1);
                this.updateZoomDisplay();
            });
        }

        if (this.config.toggleLayoutButton) {
            const layoutSelect = this.container.querySelector(`#${this.config.instanceId}-layout-select`);
            layoutSelect.addEventListener("change", (event) => {
                const layout = event.target.value;
                if (layout === "grid") this.applyGridLayout();
                else if (layout === "vertical") this.applyVerticalLayout();
                else if (layout === "horizontal") this.applyHorizontalLayout();
            });
        }
    }

    // Update the zoom level display.
    updateZoomDisplay() {
        if (!this.config.toggleZoom) return;
        const zoomLevelEl = this.container.querySelector(`#${this.config.instanceId}-zoom-level`);
        if (zoomLevelEl) {
            const zoomPercent = Math.round(this.graph.zoom() * 100);
            zoomLevelEl.innerText = `${zoomPercent}%`;
        }
    }

    // Setup the minimap (area map) overlay.
    setupAreaMap(canvasEl) {
        const areaMapContainer = document.createElement("div");
        areaMapContainer.className = "area-map";
        areaMapContainer.style.position = "absolute";
        areaMapContainer.style.bottom = "10px";
        areaMapContainer.style.right = "10px";
        areaMapContainer.style.width = "200px";
        areaMapContainer.style.height = "120px";
        canvasEl.appendChild(areaMapContainer);
        const miniMap = new this.MiniMap({
            container: areaMapContainer,
            width: 200,
            height: 120,
            graph: this.graph
        });
        this.graph.use(miniMap);

    }

    getState() {
        return {
            isDirty: this.isDirty,
            visible: true, //check modal visibility
            data: this.getData(),
        }
    }


    restoreState(state) {
        this.setDirty(state.isDirty);
        this.setData(state.data);
        if (state.visible) {
            this.show();
        }
    }


    async toggleCoPilot() {
        // If the copilot modal hasn't been created yet, create it.
        if (!this.coPilotModal) {
            this.coPilotModal = document.createElement("div");
            this.coPilotModal.className = "modal fade workflow-copilot-modal";
            this.coPilotModal.setAttribute("tabindex", "-1");
            this.coPilotModal.innerHTML = `
                <div class="modal-dialog modal-lg modal-dialog-centered">
                    <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Workflow CoPilot</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body" id="${this.config.instanceId}-copilot-body"></div>
                    </div>
                </div>`;
            document.body.appendChild(this.coPilotModal);
            // Create the co-pilot component if it hasn’t been created.
            if (!this.coPilot) {
                this.coPilot = await this.createManagedComponent("AdvancedAIWorkspace", `#${this.config.instanceId}-copilot-body`, {
                    instanceId: `${this.config.instanceId}-copilot`,
                    agentName: 'WorkflowCoPilot',
                    singleAgentMode: true,
                    height: 'container'
                });
            }
        }
        // Toggle the copilot modal using Bootstrap's Modal API.
        const bpModal = bootstrap.Modal.getInstance(this.coPilotModal) || new bootstrap.Modal(this.coPilotModal);
        if (this.coPilotModal.classList.contains("show")) {
            bpModal.hide();
        } else {
            bpModal.show();
        }
    }

    destroy() {
        if (this.coPilot) {
            this.coPilot.destroy();
            this.coPilot = null;
        }
        if (this.coPilotModal) {
            this.coPilotModal.remove();
            this.coPilotModal = null;
        }
        if (this.container) {
            this.container.remove();
            this.container = null;
        }

        window.removeEventListener("keydown", this._boundKeyboardHandler);
        super.destroy();
    }


    handleCmdSetActiveWorkflowConfig(tools) {
        this.log.info("handle cmd set active workflow config", tools);
        this.setData(tools);
        this.setDirty(true);
    }

    handleCmdFetchActiveWorkflowConfig() {
        this.log.info("handle cmd fetch active workflow config");
        return this.getData();
    }

    async handleCmdReloadTools() {
        this.log.info("handle cmd reload tools");
        this.appClient.clientCommandHandler.unregisterCommand("set-active-workflow-config");
        this.appClient.clientCommandHandler.unregisterCommand("fetch-active-workflow-config");
        this.appClient.clientCommandHandler.unregisterCommand("reload-tools");
        await this.loadAvailableTools();
        this.renderToolPalette();
    }
}